import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import Header from '../components/Header'
import SuburbInput from '../components/SuburbInput'
import ContactWarning from '../components/ContactWarning'
import { WANTED_CATEGORIES, looksLikeRegisteredVehicle, describeWantedError, WANTED_EXPIRY_DAYS } from '../lib/wanted'
import { stateFromPostcode } from '../lib/areas'
import { checkAndLogContact } from '../lib/contactFlags'
import { detectContactDetails, blockingKinds, WANTED_CONTACT_MODE } from '../lib/contactDetection'
import { track } from '../lib/analytics'

// /wanted/new — post a Wanted request (signed-in users; ProtectedRoute).
// Can be pre-filled from a search that found nothing: ?title=…&loc=…&cat=…
export default function WantedNew() {
const { user } = useAuth()
const navigate = useNavigate()
const [searchParams] = useSearchParams()
const loc = (searchParams.get('loc') || '').trim()
const [form, setForm] = useState({
title: (searchParams.get('title') || '').slice(0, 80),
category_id: WANTED_CATEGORIES.some(c => String(c.id) === searchParams.get('cat')) ? searchParams.get('cat') : '',
suburb: /^\d{4}$/.test(loc) ? '' : loc,
postcode: /^\d{4}$/.test(loc) ? loc : '',
lat: null,
lng: null,
dates_flexible: false,
date_from: '',
date_to: '',
budget: '',
budget_period: 'total',
note: '',
})
const [error, setError] = useState('')
const [saving, setSaving] = useState(false)
const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setError('') }
const today = new Date().toISOString().split('T')[0]

const handleSubmit = async () => {
setError('')
const title = form.title.trim()
if (title.length < 3) { setError('Please say what you\'re looking for (at least 3 characters).'); return }
if (!form.category_id) { setError('Please choose a category.'); return }
if (looksLikeRegisteredVehicle(title, form.category_id)) { setError(describeWantedError({ message: 'blocked_vehicle' })); return }
if (form.suburb.trim().length < 2) { setError('Please enter your suburb.'); return }
if (!/^\d{4}$/.test(form.postcode)) { setError('Please enter your 4-digit postcode.'); return }
if (!form.dates_flexible && !form.date_from) { setError('Please choose the dates you need it, or tick "I\'m flexible".'); return }
if (!form.dates_flexible && form.date_to && form.date_to < form.date_from) { setError('The end date is before the start date.'); return }
const budget = form.budget === '' ? null : Number(form.budget)
if (budget !== null && (!Number.isFinite(budget) || budget < 0)) { setError('Budget must be a number.'); return }

// Contact details: blocked on Wanted posts (public page). Logged for Admin → Flags.
const contact = detectContactDetails(`${title}\n${form.note}`)
if (WANTED_CONTACT_MODE === 'block' && blockingKinds(contact).length) {
checkAndLogContact('wanted', `${title}\n${form.note}`)
setError('Not posted yet — remove the contact details above first. People can message you through HireIt.')
return
}

setSaving(true)
// "Call me" with no actual details still posts, but is logged
await checkAndLogContact('wanted', `${title}\n${form.note}`)
const { data, error: err } = await supabase.from('wanted_requests').insert({
user_id: user.id,
title,
category_id: parseInt(form.category_id, 10),
suburb: form.suburb.trim(),
postcode: form.postcode,
state: stateFromPostcode(form.postcode),
lat: form.lat,
lng: form.lng,
dates_flexible: form.dates_flexible,
date_from: form.dates_flexible ? null : form.date_from,
date_to: form.dates_flexible ? null : (form.date_to || form.date_from),
budget,
budget_period: form.budget_period,
note: form.note.trim() || null,
}).select('id').single()
setSaving(false)
if (err) { setError(describeWantedError(err)); return }
track('wanted_posted', { category: WANTED_CATEGORIES.find(c => String(c.id) === String(form.category_id))?.slug })
navigate(`/wanted?posted=${data.id}`)
}

return (
<div>
<Header />
<div className="container" style={{ maxWidth: 560, padding: '1.5rem 1rem' }}>
<Link to="/wanted" style={{ fontSize: 13, color: 'var(--primary)', textDecoration: 'none' }}>← Wanted board</Link>
<h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--navy)', margin: '8px 0 4px' }}>Post a Wanted request</h1>
<p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>Tell people nearby what you'd like to hire. Only your first name and suburb are shown. Requests close after {WANTED_EXPIRY_DAYS} days.</p>

<div className="card card-body">
<div className="form-group">
<label className="form-label">What are you looking for?</label>
<input className="form-control" maxLength={80} placeholder="e.g. Kayak for the weekend" value={form.title} onChange={e => set('title', e.target.value)} />
</div>
<div className="form-group">
<label className="form-label">Category</label>
<select className="form-control" value={form.category_id} onChange={e => set('category_id', e.target.value)}>
<option value="">Choose a category</option>
{WANTED_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
</select>
<div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 4 }}>Trailers & caravans, registered vehicles and medical equipment can't be requested yet.</div>
</div>
<div className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
<div className="form-group">
<label className="form-label">Suburb</label>
<SuburbInput value={form.suburb} onChange={v => set('suburb', v)} onPlace={p => setForm(f => ({ ...f, suburb: p.suburb || f.suburb, postcode: p.postcode || f.postcode, lat: p.lat ?? null, lng: p.lng ?? null }))} />
</div>
<div className="form-group">
<label className="form-label">Postcode</label>
<input className="form-control" inputMode="numeric" maxLength={4} placeholder="e.g. 7216" value={form.postcode} onChange={e => set('postcode', e.target.value.replace(/\D/g, ''))} />
</div>
</div>
<div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: -8, marginBottom: 12 }}>Suburb only — please don't add your street address.</div>

<div className="form-group">
<label className="form-label">When do you need it?</label>
<label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 8, cursor: 'pointer' }}>
<input type="checkbox" checked={form.dates_flexible} onChange={e => set('dates_flexible', e.target.checked)} /> I'm flexible
</label>
{!form.dates_flexible && (
<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
<input className="form-control" type="date" min={today} value={form.date_from} onChange={e => set('date_from', e.target.value)} aria-label="From" />
<input className="form-control" type="date" min={form.date_from || today} value={form.date_to} onChange={e => set('date_to', e.target.value)} aria-label="To" />
</div>
)}
</div>

<div className="form-group">
<label className="form-label">Budget <span style={{ fontWeight: 400, color: 'var(--text-faint)' }}>(optional)</span></label>
<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
<input className="form-control" inputMode="decimal" placeholder="$" value={form.budget} onChange={e => set('budget', e.target.value.replace(/[^\d.]/g, ''))} />
<select className="form-control" value={form.budget_period} onChange={e => set('budget_period', e.target.value)}>
<option value="total">total</option>
<option value="per day">per day</option>
<option value="per week">per week</option>
</select>
</div>
</div>

<div className="form-group">
<label className="form-label">Short note <span style={{ fontWeight: 400, color: 'var(--text-faint)' }}>(optional)</span></label>
<textarea className="form-control" rows={3} maxLength={500} placeholder="Anything that helps — size, what it's for, pick-up times…" value={form.note} onChange={e => set('note', e.target.value)} />
</div>

<ContactWarning text={`${form.title}\n${form.note}`} block={WANTED_CONTACT_MODE === 'block'} />
{error && <div className="alert alert-error">{error}</div>}
<button className="btn btn-gradient btn-lg btn-full" onClick={handleSubmit} disabled={saving}>{saving ? 'Posting…' : 'Post request'}</button>
</div>
</div>
</div>
)
}
